import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { useGraphColors } from "@core";
import { useSubstationSensors } from "@core/apis/shared";

import { SensorTableCard } from "../SensorTable";
import type { SensorEditingCapability } from "../SensorTable/types";

type SensorTableSectionProps = Readonly<{
  substationId: string;
  /**
   * Optional sensor editing support from the host app.
   * Omit to keep the table read-only.
   */
  editing?: SensorEditingCapability;
}>;

const twoWeeksDateRange = {
  startDate: DateTime.now().minus({ weeks: 2 }).startOf("day"),
  endDate: DateTime.now().endOf("hour"),
};

export function SensorTableSection({ substationId, editing }: SensorTableSectionProps) {
  const { t } = useTranslation(["propai_properties", "_common", "propai_properties"]);
  const graphColors = useGraphColors();
  const { sensors, isLoading } = useSubstationSensors(substationId);

  const chartValueTypeMapping = useMemo(
    () => ({
      living_air_temperature: {
        yAxis: 0,
        suffix: "°C",
        title: "Temperature (°C)",
        color: graphColors[0],
      },
      indoor_relative_humidity: {
        yAxis: 1,
        suffix: "%",
        title: "Humidity (%)",
        color: graphColors[1],
      },
      supplytemp: {
        yAxis: 0,
        suffix: "°C",
        title: "Supply Temperature (°C)",
        color: graphColors[2],
      },
      returntemp: {
        yAxis: 0,
        suffix: "°C",
        title: "Return Temperature (°C)",
        color: graphColors[3],
      },
      radiator_system_supply_temp: {
        yAxis: 0,
        suffix: "°C",
        title: "Radiator Supply Temperature (°C)",
        color: graphColors[4],
      },
      water_tank_temperature: {
        yAxis: 0,
        suffix: "°C",
        title: "Water Tank Temperature (°C)",
        color: graphColors[5],
      },
    }),
    [graphColors]
  );

  return (
    <SensorTableCard
      substationId={substationId}
      sensors={sensors}
      isLoading={isLoading}
      editing={editing}
      cardTitle={t("all_sensors")}
      selectionConfig={{
        mode: "uid" as const,
        enableMultiSelection: true,
      }}
      columnConfig={{
        columns: {
          external_id: true,
          sensor_type: true,
          apartment: true,
          floor: true,
          location: false,
          street_address: true,
        },
        headers: {
          external_id: t("external_id", { ns: "propai_properties" }),
          sensor_type: t("sensor_type", { ns: "propai_properties" }),
          apartment: t("apartment", { ns: "propai_properties" }),
          floor: t("floor", { ns: "propai_properties" }),
          location: t("_common:text_location", { ns: "propai_properties" }),
          street_address: t("street_address", { ns: "propai_properties" }),
        },
      }}
      chartConfig={{
        valueTypeMapping: chartValueTypeMapping,
        dateRange: twoWeeksDateRange,
      }}
      showChart={true}
      pagination={{
        pageSize: 25,
        showPagination: true,
      }}
    />
  );
}
